/**
 * Learn — two ways the agent gets better without the adviser writing anything:
 *   research   the builder reads current OFFICIAL sources for the field (the approved sites of iMaven's
 *              reference agent) and brings verified findings back as proposals with URLs, plus dated
 *              facts for live check;
 *   learning   the builder reads the agent's recent REAL conversations with clients (personal data masked)
 *              and proposes what to improve; on request, or weekly if switched on.
 * Both cost the adviser tokens; results arrive as proposals and in the conversation.
 *
 * API: POST /agent-builder/research {question} · GET|PUT /agent-builder/learning · POST /agent-builder/learning/review
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../Context/ToastContext';
import { Box, Row, Help, Muted, PrimaryBtn, GhostBtn, TextArea, errText, fmtWhen } from './styles';

export default function LearnPanel({ api, onStarted }) {
  const { showToast } = useToast();
  const [question, setQuestion] = useState('');
  const [learning, setLearning] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { const r = await api.get('/agent-builder/learning'); setLearning(r.data); } catch { setLearning(null); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const research = async () => {
    setBusy('research');
    try {
      await api.post('/agent-builder/research', { question: question.trim() });
      showToast('Research started — verified findings come back as proposals in a few minutes.', 'success');
      setQuestion('');
      onStarted?.();
    } catch (e) { showToast(errText(e, 'The research could not start'), 'error'); } finally { setBusy(''); }
  };
  const review = async () => {
    setBusy('review');
    try {
      const r = await api.post('/agent-builder/learning/review', {});
      showToast(`Reading ${r.data.conversations} recent conversation${r.data.conversations === 1 ? '' : 's'}…`, 'success');
      onStarted?.();
      load();
    } catch (e) { showToast(errText(e, 'Could not start the review'), 'error'); } finally { setBusy(''); }
  };
  const toggleAuto = async () => {
    setBusy('auto');
    try { const r = await api.put('/agent-builder/learning', { auto: !learning?.auto }); setLearning(l => ({ ...l, auto: r.data.auto })); } catch (e) { showToast(errText(e, 'Could not change it'), 'error'); } finally { setBusy(''); }
  };

  return (
    <div>
      <Box>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Research official sources</div>
        <Help style={{ marginBottom: 8 }}>The builder reads only the approved official sites and data sources for your field, and brings back what it could verify — with the link and date — as proposals. Figures and rules your agent depends on are offered as live-check facts, so they are re-checked before consultations.</Help>
        <TextArea rows={3} placeholder="What should it research? e.g. “the current rules for written estimates and repair warranties in Ontario”" value={question} onChange={e => setQuestion(e.target.value)} />
        <Row style={{ marginTop: 8, justifyContent: 'space-between' }}>
          <Muted>Uses your AI tokens (a few minutes of searching and reading).</Muted>
          <PrimaryBtn disabled={busy === 'research' || question.trim().length < 10} onClick={research}>Research</PrimaryBtn>
        </Row>
      </Box>

      <Box>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Learn from your real conversations</div>
        <Help style={{ marginBottom: 8 }}>The builder reads your agent’s recent finished conversations with clients and proposes improvements: questions it repeated, things clients said that it ignored, routes it missed, statements that were wrong. Personal details are masked and never go into your agent. Each conversation is read once.</Help>
        {learning && (
          <Help style={{ marginBottom: 8 }}>
            New conversations to learn from: <b>{learning.newConversations}</b> · read so far: {learning.reviewed}{learning.lastRunAt ? ` · last review ${fmtWhen(learning.lastRunAt)}` : ''}
          </Help>
        )}
        <Row style={{ justifyContent: 'space-between' }}>
          <GhostBtn disabled={!!busy} onClick={toggleAuto}>{learning?.auto ? '✓ Weekly review is on — turn off' : 'Review new conversations every week'}</GhostBtn>
          <PrimaryBtn disabled={!!busy || !learning?.newConversations} onClick={review}>Review now</PrimaryBtn>
        </Row>
        <Help>Uses your AI tokens. With the weekly review on, you get an email when there is something to approve.</Help>
      </Box>
    </div>
  );
}
